import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ContactDetailsBlockType } from "../../types";
import { useCreateBlocks } from "../../hooks/use-create-blocks";
import { useResolvedView } from "../../hooks/use-resolved-view";
import { isRegisteredBlockType } from "../../utils";
import type { SelectContactDetailsBlockDialogProps } from "./SelectContactDetailsBlockDialog";

export type UseSelectContactDetailsBlockDialogArgs = Pick<
  SelectContactDetailsBlockDialogProps,
  "viewId" | "onViewMaterialized" | "onBlocksAdded"
>;

export const useSelectContactDetailsBlockDialog = ({
  viewId,
  onViewMaterialized,
  onBlocksAdded,
}: UseSelectContactDetailsBlockDialogArgs) => {
  const { t } = useTranslation("contacts");
  const [selectedBlocks, setSelectedBlocks] = useState<ContactDetailsBlockType[]>([]);
  const [isAddingBlocks, setIsAddingBlocks] = useState(false);

  const resolvedView = useResolvedView(viewId);
  const createBlocks = useCreateBlocks({ viewId, onViewMaterialized });

  const existingBlockCounts = useMemo(() => {
    if (resolvedView.mode === "loading" || resolvedView.mode === "error") return undefined;

    return resolvedView.blocks.reduce<Partial<Record<ContactDetailsBlockType, number>>>((counts, block) => {
      if (!isRegisteredBlockType(block.type)) return counts;
      return { ...counts, [block.type]: (counts[block.type] ?? 0) + 1 };
    }, {});
  }, [resolvedView]);

  const toggleBlock = useCallback((blockType: ContactDetailsBlockType) => {
    setSelectedBlocks((prev) => {
      const isSelected = prev.includes(blockType);
      if (isSelected) {
        return prev.filter((block) => block !== blockType);
      }
      return [...prev, blockType];
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedBlocks([]), []);

  const addSelectedBlocks = useCallback(() => {
    const blockTypes = selectedBlocks;
    setIsAddingBlocks(true);

    createBlocks(blockTypes)
      .then(
        () => {
          setSelectedBlocks([]);
          onBlocksAdded?.(blockTypes);
        },
        () => toast.error(t("The sections could not be added. Please try again."))
      )
      .finally(() => setIsAddingBlocks(false));
  }, [createBlocks, onBlocksAdded, selectedBlocks, t]);

  return {
    selectedBlocks,
    existingBlockCounts,
    isAddingBlocks,
    toggleBlock,
    clearSelection,
    addSelectedBlocks,
  };
};
